import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import api, { errorMessage } from "../lib/api";
import { isPasswordStrong, PASSWORD_REQUIREMENTS } from "../lib/password";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Lock, Eye, EyeOff, Check, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(token ? "form" : "invalid");

  const submit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(newPassword)) {
      toast.error("Le mot de passe ne respecte pas les critères de sécurité");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setStatus("success");
    } catch (e) {
      if (e.response?.status === 400) {
        setStatus("invalid");
      } else {
        toast.error(errorMessage(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      kicker="Des pharmacies plus fortes, pour des communautés en meilleure santé."
      headlineLines={["Ensemble pour", "une meilleure santé."]}
      quote="Des outils modernes pour un impact réel sur le terrain."
    >
      {status === "invalid" && (
        <div className="text-center space-y-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 mx-auto">
            <AlertCircle className="text-red-500" size={26} />
          </div>
          <h2 className="font-serif-display text-2xl font-bold text-slate-900">Lien invalide</h2>
          <p className="text-sm text-slate-500">
            Ce lien de réinitialisation est invalide ou a expiré.
          </p>
          <Link to="/forgot-password" className="text-sm font-medium text-jamora hover:underline">
            Demander un nouveau lien
          </Link>
        </div>
      )}

      {status === "success" && (
        <div className="text-center space-y-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-jamora-50 mx-auto">
            <CheckCircle2 className="text-jamora" size={26} />
          </div>
          <h2 className="font-serif-display text-2xl font-bold text-slate-900">Mot de passe modifié</h2>
          <p className="text-sm text-slate-500">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center w-full h-11 rounded-md bg-jamora hover:bg-jamora-hover text-white text-sm font-medium"
          >
            Se connecter
          </Link>
        </div>
      )}

      {status === "form" && (
        <>
          <div className="flex flex-col items-center text-center mb-8">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-jamora-50 mb-5">
              <Lock size={26} className="text-jamora" />
            </div>
            <h2 className="font-serif-display text-3xl font-bold text-slate-900">Choisir un nouveau mot de passe</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-[340px]">
              Votre nouveau mot de passe doit être différent de l'ancien et respecter les critères de sécurité.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="new-password"
                  data-testid="input-reset-new-password"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Au moins 8 caractères"
                  className="pl-10 pr-10 h-11"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={showNew ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="confirm-password"
                  data-testid="input-reset-confirm-password"
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Répétez votre mot de passe"
                  className="pl-10 pr-10 h-11"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={showConfirm ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <ul className="rounded-lg bg-jamora-50 p-4 space-y-2">
              {PASSWORD_REQUIREMENTS.map((req) => {
                const met = req.test(newPassword);
                return (
                  <li key={req.key} className="flex items-center gap-2 text-sm">
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full flex-shrink-0 ${
                        met ? "bg-jamora" : "bg-slate-300"
                      }`}
                    >
                      <Check size={11} className="text-white" strokeWidth={3} />
                    </span>
                    <span className={met ? "text-slate-700" : "text-slate-400"}>{req.label}</span>
                  </li>
                );
              })}
            </ul>

            <Button
              type="submit"
              data-testid="btn-reset-submit"
              disabled={loading}
              className="w-full h-11"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : "Réinitialiser mon mot de passe"}
              {!loading && <ArrowRight size={16} />}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
